import type { QueryClient } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { authClient } from "@platform/auth/client";

// Permission shape accepted by the shared RBAC (for example { apiKey: ["create"] }).
type CheckPermissionArg = Parameters<typeof authClient.organization.checkRolePermission>[0];
export type PermissionRequest = CheckPermissionArg["permissions"];

// The route guards (ensureQueryData) and the auth context (useQuery) share these
// keys, so each lookup runs one fetch.
// A failed request resolves to null and counts as unauthenticated. A throw out of
// beforeLoad crashes the route.
export const sessionQueryOptions = queryOptions({
	queryKey: ["auth", "session"],
	queryFn: async () => {
		try {
			return (await authClient.getSession()).data ?? null;
		} catch {
			return null;
		}
	},
});

export const activeMemberQueryOptions = queryOptions({
	queryKey: ["auth", "active-member"],
	queryFn: async () => {
		try {
			return (await authClient.organization.getActiveMember()).data ?? null;
		} catch {
			return null;
		}
	},
});

export async function requireSession(queryClient: QueryClient) {
	const session = await queryClient.ensureQueryData(sessionQueryOptions);
	if (!session?.user) throw redirect({ to: "/login" });
	return session;
}

// Lawline staff hold the cross-tenant admin role. The server
// requirePlatformAdmin is the real enforcement.
export async function requirePlatformAdmin(queryClient: QueryClient) {
	const session = await requireSession(queryClient);
	if (session.user.role !== "admin") throw redirect({ to: "/" });
}

// The server requirePermission is the real enforcement.
export async function requirePermission(queryClient: QueryClient, permission: PermissionRequest) {
	await requireSession(queryClient);
	const member = await queryClient.ensureQueryData(activeMemberQueryOptions);
	const allowed = member?.role
		? authClient.organization.checkRolePermission({
				role: member.role,
				permissions: permission,
			} as CheckPermissionArg)
		: false;
	if (!allowed) throw redirect({ to: "/" });
}
